import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef, PropsWithChildren,
} from 'react';
import { setAuthSnapshot, clearAuthSnapshot } from '../prefetch/api';
import { getAuthMe } from '../api/clientsApi';
import { getCompanySlugFromLocation, resolveActiveCompanySlug, getRuntimeEnvFromHostname } from '../utils/companySlug';
import { isSuperadminPath } from '../utils/routeHelpers';
import { onInvalidate } from '../lib/live';
import { useSocketSubscription } from '../lib/socketManager';

// no Router needed
function usePathname(): string {
  const [path, setPath] = useState<string>(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const patch = (method: 'pushState' | 'replaceState') => {
      const original = window.history[method];
      return function (this: History, ...args: any[]) {
        const ret = (original as any).apply(window.history, args);
        window.dispatchEvent(new Event('locationchange'));
        return ret;
      };
    };
    const restorePush = window.history.pushState.bind(window.history);
    const restoreReplace = window.history.replaceState.bind(window.history);
    (window.history as any).pushState = patch('pushState');
    (window.history as any).replaceState = patch('replaceState');

    const onChange = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onChange);
    window.addEventListener('locationchange', onChange);
    return () => {
      window.history.pushState = restorePush;
      window.history.replaceState = restoreReplace;
      window.removeEventListener('popstate', onChange);
      window.removeEventListener('locationchange', onChange);
    };
  }, []);
  return path;
}

function isLoginRoute(pathname: string) {
  return /^\/(?:superadmin|[^/]+)\/login$/.test(pathname);
}

function resolveCompanySlug(pathname: string): string | null {
  // CRITICAL: Superadmin routes do NOT have a company slug
  if (isSuperadminPath(pathname)) {
    return null;
  }
  
  // Use centralized helper - URL is always source of truth
  const slugFromUrl = getCompanySlugFromLocation();
  if (slugFromUrl) {
    return slugFromUrl;
  }
  
  // If no URL slug, use centralized resolver
  const env = getRuntimeEnvFromHostname();
  const resolved = resolveActiveCompanySlug({ env });
  return resolved || null;
}

export type ClientType = {
  _id: string;
  fullName?: string;
  email?: string;
  phone?: string;
  company?: string;
  totalSessions?: number;
  usedSessions?: number;
  startDate?: string;
  endDate?: string;
  role?: 'client' | 'admin' | 'manager' | 'agent' | 'superadmin' | 'vendor';
  vendorId?: string;
  adminId?: string;
  managerIds?: string[];
  agentId?: string;
  businessId?: string;
  permissions?: string[];
  passwordHash?: string;
  lastLogin?: string;
  createdAt?: string;
  updatedAt?: string;
  activeTenantId?: string;
  activeRole?: string;
  status?: 'active' | 'inactive' | 'pending';
  isBlocked?: boolean;
  language?: 'he' | 'en' | 'ar';
  preferredLanguage?: 'he' | 'en' | 'ar';
  trialInfo?: {
    isTrial: boolean;
    trialStartedAt: string | null;
    trialExpiresAt: string | null;
    daysTotal: number;
    daysUsed: number;
    daysLeft: number;
    isExpired: boolean;
  };
};

type ClientContextType = {
  client: ClientType | null;
  setClient: (c: ClientType | null) => void;
  refreshClient: () => Promise<void>;
  loading: boolean;
};

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const pathname = usePathname();
  // Initialize from localStorage immediately for fast initial render
  // BUT: Validate that stored client matches current company slug
  const [client, setClient] = useState<ClientType | null>(() => {
    try {
      const stored = localStorage.getItem('client');
      if (!stored) {
        return null;
      }
      
      const parsed = JSON.parse(stored);
      const currentSlug = resolveCompanySlug(pathname);
      
      // 🔥 CRITICAL FIX: Only check for superadmin vs tenant mismatch
      // Do NOT compare company name with slug - they are different things!
      // The company field stores the company name (e.g., "WJS Digital Innovations")
      // while currentSlug is the URL slug (e.g., "test")
      // The API will return the correct client data for the current slug
      
      // If switching from superadmin to tenant (or vice versa), clear stored client
      if (currentSlug) {
        const isStoredSuperadmin = parsed?.role === 'superadmin';
        const isCurrentSuperadmin = currentSlug === 'superadmin';
        if (isStoredSuperadmin !== isCurrentSuperadmin) {
          localStorage.removeItem('client');
          return null;
        }
      }
      
      return parsed;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false); // Start as false since we have cached data
  
  // Use ref to access current client value without creating dependency
  // This prevents infinite loops when refreshClient updates client
  const clientRef = useRef<ClientType | null>(client);
  
  // Keep ref in sync with state
  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  const refreshClient = useCallback(async () => {
    // CRITICAL: Superadmin routes do NOT use company client context
    // ClientContext is company-scoped, superadmin has its own auth flow
    if (isSuperadminPath(pathname)) {
      // For superadmin, we still need to check if user is authenticated
      // But we don't fetch company-scoped client data
      // The client data should come from superadmin auth endpoints
      setLoading(false);
      return;
    }
    
    // Use ref to access current client without creating dependency
    const currentClient = clientRef.current;
    
    // Don't set loading to true immediately - let the page render first
    // Only set loading if we don't have client data yet
    if (!currentClient) {
      setLoading(true);
    }
    
    try {
      if (isLoginRoute(pathname)) {
        setClient(null);
        clearAuthSnapshot();
        setLoading(false);
        return;
      }
      const slug = resolveCompanySlug(pathname);
      if (!slug) {
        setClient(null);
        clearAuthSnapshot();
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setClient(null);
        clearAuthSnapshot();
        setLoading(false);
        return;
      }

      localStorage.setItem('currentCompany', slug);
      try {
        const currentClientForLog = clientRef.current;
        
        const data = await getAuthMe(slug);
        // getAuthMe now returns client directly (not wrapped in { client })
        const clientData = (data?.client ?? data) as ClientType;
        
      
        // Check if user is blocked - if so, show modal instead of immediate logout
        if (clientData?.isBlocked === true) {
          // Emit window event to trigger BlockedUserSocketModal
          window.dispatchEvent(new CustomEvent('user:blocked', { 
            detail: {
              message: 'המשתמש נחסם. יש לפנות למנהל המערכת.',
              isBlocked: true,
              userId: clientData?._id || null
            }
          }));
          // Don't set client to null or redirect - let the modal handle logout
          setLoading(false);
          return;
        }
        
        // Check if trial has expired - redirect to trial-expired page
        if (clientData?.trialInfo?.isExpired === true) {
          const slug = resolveCompanySlug(pathname);
          if (slug) {
            window.location.href = `/${slug}/trial-expired`;
          }
          setLoading(false);
          return;
        }
        
        // 🔥 CRITICAL FIX: Only update client if data has actually changed
        // This prevents infinite loops when the same data is set repeatedly
        const currentClient = clientRef.current;
        // Quick check: if no current client, always update
        // If IDs differ, always update
        // If IDs match, do deep comparison to catch field changes
        const hasChanged = !currentClient || 
          currentClient._id !== clientData._id ||
          JSON.stringify(currentClient) !== JSON.stringify(clientData);
        
        if (hasChanged) {
          setClient(clientData);
          // Apply effective user language (preferredLanguage ?? language) so we never rely on only one field
          const effectiveLang = clientData?.preferredLanguage ?? clientData?.language;
          if (effectiveLang === 'he' || effectiveLang === 'en' || effectiveLang === 'ar') {
            window.dispatchEvent(new CustomEvent('app:setLanguage', { detail: effectiveLang }));
          }
        }
        
        // Update localStorage with fresh client data (including permissions)
        // This is synchronous but very fast (microseconds)
        try {
          localStorage.setItem('client', JSON.stringify(clientData));
        } catch (e) {
          // Failed to save to localStorage
        }
        
        // Set auth snapshot for prefetching
        setAuthSnapshot({
          role: clientData?.role,
          permissions: clientData?.permissions || []
        });
      } catch (err: any) {
        // 🔥 CRITICAL: Handle blocked company/user - this is the ONLY valid auto-logout scenario
        // Check if company is blocked (403 with companyBlocked flag)
        if (err?.response?.status === 403 && err?.response?.data?.companyBlocked === true) {
          const slug = resolveCompanySlug(pathname) || 'test';
          
          // Emit blocked event to trigger BlockedUserSocketModal
          window.dispatchEvent(new CustomEvent('user:blocked', { 
            detail: {
              message: 'החברה נחסמה. יש לפנות למנהל המערכת.',
              isBlocked: true,
              userId: null,
              source: 'ClientContext-companyBlocked'
            }
          }));
          
          // DO NOT logout here - let BlockedUserSocketModal handle it with countdown
          setLoading(false);
          return;
        }
        
        // 🔥 CRITICAL: Handle blocked user - this is the ONLY valid auto-logout scenario
        // Handle 403 blocked user response - show modal with countdown
        if (err?.response?.status === 403 && err?.response?.data?.isBlocked === true) {
          const slug = resolveCompanySlug(pathname) || 'test';
          
          // Emit blocked event to trigger BlockedUserSocketModal
          window.dispatchEvent(new CustomEvent('user:blocked', { 
            detail: {
              message: 'המשתמש נחסם. יש לפנות למנהל המערכת.',
              isBlocked: true,
              userId: null,
              source: 'ClientContext-userBlocked'
            }
          }));
          
          // DO NOT logout here - let BlockedUserSocketModal handle it with countdown
          setLoading(false);
          return;
        }
        
        // 🔥 CRITICAL: Check for real auth errors vs server errors
        const status = err?.response?.status;
        const errorCode = err?.response?.data?.code || err?.response?.data?.errorCode;
        const errorMessage = err?.response?.data?.error || err?.response?.data?.message || '';
        
        // 🔥 CRITICAL: NEVER clear client on 500 errors - these are server errors, not auth errors
        if (status === 500) {
          // Keep existing client data from localStorage if available
          const storedClient = localStorage.getItem('client');
          if (storedClient) {
            try {
              const parsed = JSON.parse(storedClient);
              setClient(parsed);
            } catch (e) {
              // If stored client is invalid, just keep loading state
            }
          }
          setLoading(false);
          return;
        }
        
        // Only clear client on REAL auth errors (401 with specific error codes)
        // NOTE: OTHER_SESSION_ACTIVE is NOT treated as an auth error - user should stay logged in
        const isAuthError = status === 401 && (
          errorCode === 'INVALID_TOKEN' ||
          errorCode === 'SESSION_EXPIRED' ||
          errorCode === 'SESSION_REVOKED' ||
          // REMOVED: errorCode === 'OTHER_SESSION_ACTIVE' - conflict sessions should NOT trigger logout
          errorMessage.includes('Invalid token') ||
          errorMessage.includes('Token expired') ||
          errorMessage.includes('Session expired') ||
          errorMessage.includes('Unauthorized')
        );
        
        if (isAuthError) {
          setClient(null);
          clearAuthSnapshot();
          setLoading(false);
          return;
        }
        
        // For other errors (400, 404, etc.), keep existing client data
        
        // Keep existing client data from localStorage if available
        // 🔥 CRITICAL: If we have a valid token, keep the client even if storedClient is missing
        // This prevents clearing client on server errors when localStorage might be temporarily empty
        const token = localStorage.getItem('token');
        const hasValidToken = token && !token.includes('null') && !token.includes('undefined');
        const currentClient = clientRef.current;
        
        const storedClient = localStorage.getItem('client');
        if (storedClient) {
          try {
            const parsed = JSON.parse(storedClient);
            // Only update if different to prevent loops
            if (JSON.stringify(parsed) !== JSON.stringify(currentClient)) {
              setClient(parsed);
            }
          } catch (e) {
            // If stored client is invalid but we have a valid token, keep current client state
            if (hasValidToken && currentClient) {
              // Keep current client, don't clear it
            } else {
              setClient(null);
              clearAuthSnapshot();
            }
          }
        } else {
          // No stored client, but if we have a valid token and current client, keep it
          if (hasValidToken && currentClient) {
            // Keep current client, don't clear it
          } else {
            setClient(null);
            clearAuthSnapshot();
          }
        }
        setLoading(false);
      }
    } catch (err: any) {
      setClient(null);
      clearAuthSnapshot();
      setLoading(false);
    }
  }, [pathname]); // 🔥 CRITICAL FIX: Removed 'client' dependency to prevent infinite loops

  // Only refresh on mount or when pathname changes (not on every render)
  useEffect(() => {
    // CRITICAL: Skip company client logic for superadmin routes
    if (isSuperadminPath(pathname)) {
      return;
    }
    
    // CRITICAL: When pathname changes (e.g., from /superadmin/home to /test/home),
    // clear any stale client data that might be from the previous company
    const currentSlug = resolveCompanySlug(pathname);
    const storedClient = localStorage.getItem('client');
    
    if (storedClient && currentSlug) {
      try {
        const parsed = JSON.parse(storedClient);
        
        // 🔥 CRITICAL FIX: Only check for superadmin vs tenant mismatch
        // Do NOT compare company name with slug - they are different things!
        // The company field stores the company name (e.g., "WJS Digital Innovations")
        // while currentSlug is the URL slug (e.g., "test")
        // The API will return the correct client data for the current slug
        
        // If account type mismatch (superadmin vs tenant), clear client
        const isStoredSuperadmin = parsed?.role === 'superadmin';
        const isCurrentSuperadmin = currentSlug === 'superadmin';
        if (isStoredSuperadmin !== isCurrentSuperadmin) {
          localStorage.removeItem('client');
          setClient(null);
        }
        // Otherwise, let the API refresh handle validation - it will return correct data for the slug
      } catch (e) {
        // Error validating stored client on pathname change
      }
    }
    
    void refreshClient();
  }, [pathname, refreshClient]); // Include refreshClient for React Hook compliance

  // Listen for websocket events when company plan/features change
  // This ensures client data (which may include plan info) updates in real-time
  useEffect(() => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    if (isSuperadminPath(pathname)) {
      return; // Skip for superadmin routes
    }

    const companySlug = resolveCompanySlug(pathname);
    if (!companySlug) {
      return;
    }

    const unsubscribe = onInvalidate((payload: any) => {
      const payloadType = payload?.type;
      const payloadCompany = payload?.company;
      
      // Only handle events for this company
      if (payloadCompany !== companySlug) return;
      
      // Handle company-plan and company-features updates
      // Client data may include plan info, so we need to refresh it
      if (payloadType === 'company-plan' || payloadType === 'company-features') {
        // Refresh client data to get updated plan info
        refreshClient();
      }
    });

    return unsubscribe;
  }, [pathname, refreshClient]);

  // Listen for manager permissions updated event to refresh client data
  // This ensures that when admin updates manager permissions, the manager's client context is updated
  useSocketSubscription('manager:permissions-updated', (payload: any) => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    if (isSuperadminPath(pathname)) {
      return; // Skip for superadmin routes
    }

    const companySlug = resolveCompanySlug(pathname);
    if (!companySlug) {
      return;
    }

    const currentClient = clientRef.current;
    if (!currentClient) {
      return;
    }
    
    // Check if this event is for the current company
    const eventCompany = payload?.company;
    if (eventCompany && eventCompany !== companySlug) {
      return;
    }
    
    // Check if this event is for the current user
    const currentUserId = currentClient._id;
    const eventManagerId = payload?.managerId;
    
    if (currentUserId && eventManagerId && String(currentUserId) === String(eventManagerId)) {
      // This is for the current user - refresh client data to get updated permissions
      
      // Refresh client data to get updated permissions from server
      refreshClient();
    } else {

    }
  });

  return (
    <ClientContext.Provider value={{ client, setClient, refreshClient, loading }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error('useClient must be used within a ClientProvider');
  return ctx;
};
